
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { authService, AuthResult } from '@/services/authService';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signUp: (email: string, password: string, firstName?: string, lastName?: string) => Promise<AuthResult>;
  signIn: (email: string, password: string) => Promise<AuthResult>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<AuthResult>;
  signInWithApple: () => Promise<AuthResult>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email);
        setSession(session);
        setUser(session?.user ?? null);
        
        // Handle new user welcome email
        if (event === 'SIGNED_IN' && session?.user) {
          const userCreatedAt = new Date(session.user.created_at);
          const now = new Date();
          const timeDiff = now.getTime() - userCreatedAt.getTime();
          const isNewUser = timeDiff < 5000;
          
          if (isNewUser) {
            setTimeout(async () => {
              try {
                await supabase.functions.invoke('send-welcome-email', {
                  body: {
                    email: session.user.email,
                    firstName: session.user.user_metadata?.first_name,
                    lastName: session.user.user_metadata?.last_name,
                  }
                });
              } catch (error) {
                console.error('Erreur envoi email de bienvenue:', error);
              }
            }, 0);
          }

          // Auto redirect after successful login
          if (window.location.pathname === '/auth') {
            window.location.href = '/';
          }
        }
        
        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Session existante:', session?.user?.email || 'Aucune');
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const getErrorMessage = (error: any, action: 'signup' | 'signin') => {
    if (action === 'signup') {
      if (error.message.includes('already registered')) {
        return "Cette adresse email est déjà utilisée";
      } else if (error.message.includes('weak password')) {
        return "Le mot de passe doit contenir au moins 6 caractères";
      } else if (error.message.includes('invalid email')) {
        return "Adresse email invalide";
      }
      return "Une erreur est survenue lors de l'inscription";
    } else {
      if (error.message.includes('Invalid login credentials')) {
        return "Email ou mot de passe incorrect";
      } else if (error.message.includes('Email not confirmed')) {
        return "Veuillez confirmer votre email avant de vous connecter";
      }
      return "Identifiants incorrects";
    }
  };

  const signUp = async (email: string, password: string, firstName?: string, lastName?: string) => {
    const result = await authService.signUp(email, password, firstName, lastName);
    
    if (result.error) {
      toast({
        title: "Erreur d'inscription",
        description: getErrorMessage(result.error, 'signup'),
        variant: "destructive",
      });
    } else {
      toast({
        title: "Inscription réussie !",
        description: "Votre compte a été créé avec succès. Un email de bienvenue vous a été envoyé.",
      });
    }
    
    return result;
  };

  const signIn = async (email: string, password: string) => {
    const result = await authService.signIn(email, password);
    
    if (result.error) {
      toast({
        title: "Erreur de connexion",
        description: getErrorMessage(result.error, 'signin'),
        variant: "destructive",
      });
    } else {
      toast({
        title: "Connexion réussie !",
        description: "Bon retour sur FEEL",
      });
    }
    
    return result;
  };

  const signOut = async () => {
    await authService.signOut();
    toast({
      title: "Déconnexion réussie",
      description: "À bientôt sur FEEL !",
    });
  };

  const signInWithGoogle = async () => {
    const result = await authService.signInWithGoogle();
    
    if (result.error) {
      toast({
        title: "Erreur Google",
        description: "Impossible de se connecter avec Google. Veuillez réessayer.",
        variant: "destructive",
      });
    }
    
    return result;
  };

  const signInWithApple = async () => {
    const result = await authService.signInWithApple();
    
    if (result.error) {
      toast({
        title: "Erreur Apple",
        description: "Impossible de se connecter avec Apple",
        variant: "destructive",
      });
    }
    
    return result;
  };

  const value = {
    user,
    session,
    signUp,
    signIn,
    signOut,
    signInWithGoogle,
    signInWithApple,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
